import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchShopJson, SHOP_IMAGE_BASE } from "../components/shopApi";
import "../index.css";
import SeoManager from "../SeoManager";

function formatPrice(value) {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) {
    return "0.00";
  }

  return amount.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getDisplayCategory(product) {
  const source = `${product?.name || ""} ${product?.description || ""}`;
  if (/\bring/i.test(source)) return "Rings";
  if (/\bbracelet/i.test(source)) return "Bracelets";
  if (/\bnecklace/i.test(source)) return "Necklaces";
  if (/\bearing/i.test(source)) return "Earrings";
  if (/\bbangle/i.test(source)) return "Bangles";
  if (/\bpendant/i.test(source)) return "Pendants";
  if (/\banklet/i.test(source)) return "Anklets";
  if (/\bchain/i.test(source)) return "Chains";
  return "Other Finds";
}

function getKaratLabel(product) {
  const source = `${product?.category_name || ""} ${product?.name || ""}`.toUpperCase();
  const match = source.match(/\b(\d{1,2})\s*K\b/);
  return match ? `${match[1]}K` : "Other";
}

function getInitials(firstname, lastname) {
  const first = (firstname || "").trim().charAt(0);
  const last = (lastname || "").trim().charAt(0);
  return `${first}${last}`.trim().toUpperCase() || "AE";
}

function isVideoUrl(url) {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url || "");
}

export default function AccountExecutivePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accountExecutive, setAccountExecutive] = useState(null);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("All Brands");
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [visibleCount, setVisibleCount] = useState(20);

  useEffect(() => {
    setLoading(true);
    fetchShopJson(`/get_account_executive_details.php?id=${encodeURIComponent(id)}`)
      .then((response) => {
        if (response?.status === "success" && response.data) {
          setAccountExecutive(response.data.accountExecutive || null);
          setProducts(Array.isArray(response.data.products) ? response.data.products : []);
        } else {
          setAccountExecutive(null);
          setProducts([]);
        }
      })
      .catch((error) => {
        console.error("Failed to load account executive", error);
        setAccountExecutive(null);
        setProducts([]);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const preparedProducts = useMemo(
    () =>
      products.map((product) => ({
        ...product,
        displayCategory: getDisplayCategory(product),
        displayBrand: (product.brand || "").trim(),
        karatLabel: getKaratLabel(product),
      })),
    [products]
  );

  const brandOptions = useMemo(() => {
    const values = preparedProducts.map((item) => item.displayBrand).filter(Boolean);
    return ["All Brands", ...Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))];
  }, [preparedProducts]);

  const categoryOptions = useMemo(() => {
    const values = preparedProducts.map((item) => item.displayCategory).filter(Boolean);
    return ["All Categories", ...Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))];
  }, [preparedProducts]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const min = minPrice === "" ? null : Number.parseFloat(minPrice);
    const max = maxPrice === "" ? null : Number.parseFloat(maxPrice);

    return preparedProducts.filter((product) => {
      if (selectedBrand !== "All Brands" && product.displayBrand !== selectedBrand) {
        return false;
      }
      if (selectedCategory !== "All Categories" && product.displayCategory !== selectedCategory) {
        return false;
      }
      const price = Number.parseFloat(product.pricePHP || 0);
      if (min !== null && !Number.isNaN(min) && price < min) {
        return false;
      }
      if (max !== null && !Number.isNaN(max) && price > max) {
        return false;
      }
      if (normalizedQuery) {
        const haystack = `${product.name || ""} ${product.description || ""} ${product.product_code || ""}`.toLowerCase();
        if (!haystack.includes(normalizedQuery)) {
          return false;
        }
      }
      return true;
    });
  }, [preparedProducts, query, selectedBrand, selectedCategory, minPrice, maxPrice]);

  useEffect(() => {
    setVisibleCount(20);
  }, [query, selectedBrand, selectedCategory, minPrice, maxPrice, id]);

  if (loading) {
    return <div className="content-page"><p>Loading account executive...</p></div>;
  }

  if (!accountExecutive) {
    return (
      <div className="content-page">
        <div className="content-card content-empty">
          <h2>Account executive not found</h2>
          <button type="button" className="content-link-button" onClick={() => navigate("/")}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <SeoManager
        title={`${`${accountExecutive.firstname || ""} ${accountExecutive.lastname || ""}`.trim()} Collection`}
        description={`Browse available gold jewelry pieces uploaded by ${`${accountExecutive.firstname || ""} ${accountExecutive.lastname || ""}`.trim()} on Asher Jewels PH.`}
        path={`/account-executive/${id}`}
        image={accountExecutive.photo ? `${SHOP_IMAGE_BASE}${accountExecutive.photo}` : undefined}
        type="profile"
      />
      <button type="button" className="product-detail-back" onClick={() => navigate(-1)}>
        Back
      </button>

      <section className="ae-hero-card">
        <div className="ae-hero-card__avatar">
          {accountExecutive.photo ? (
            <img
              src={`${SHOP_IMAGE_BASE}${accountExecutive.photo}`}
              alt={`${accountExecutive.firstname} ${accountExecutive.lastname}`}
            />
          ) : (
            <span>{getInitials(accountExecutive.firstname, accountExecutive.lastname)}</span>
          )}
        </div>
        <div className="ae-hero-card__body">
          <p className="category-section__eyebrow">Account Executive</p>
          <h1>{`${accountExecutive.firstname || ""} ${accountExecutive.lastname || ""}`.trim()}</h1>
          <p>{accountExecutive.designation || "Account Executive"}</p>
          <div className="ae-hero-card__stats">
            <span>{accountExecutive.available_items || 0} available items</span>
            <span>{accountExecutive.total_items || 0} total uploads</span>
          </div>
        </div>
      </section>

      <section className="catalog-filters ae-catalog-filters">
        <div className="catalog-filter catalog-filter--search">
          <label htmlFor="aeItemSearch">Search this seller's items</label>
          <input
            id="aeItemSearch"
            type="text"
            value={query}
            placeholder="Search by item name or product code"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        <div className="catalog-filter">
          <label htmlFor="aeBrandFilter">Brand</label>
          <select id="aeBrandFilter" value={selectedBrand} onChange={(event) => setSelectedBrand(event.target.value)}>
            {brandOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="catalog-filter">
          <label htmlFor="aeCategoryFilter">Category</label>
          <select id="aeCategoryFilter" value={selectedCategory} onChange={(event) => setSelectedCategory(event.target.value)}>
            {categoryOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>

        <div className="catalog-filter">
          <label htmlFor="aeMinPrice">Min Price</label>
          <input
            id="aeMinPrice"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={minPrice}
            onChange={(event) => setMinPrice(event.target.value)}
          />
        </div>

        <div className="catalog-filter">
          <label htmlFor="aeMaxPrice">Max Price</label>
          <input
            id="aeMaxPrice"
            type="number"
            min="0"
            step="0.01"
            placeholder="999999.99"
            value={maxPrice}
            onChange={(event) => setMaxPrice(event.target.value)}
          />
        </div>
      </section>

      <div className="catalog-heading">
        <div>
          <p className="category-section__eyebrow">Available pieces</p>
          <h3>{`${filteredProducts.length} item${filteredProducts.length === 1 ? "" : "s"}`}</h3>
        </div>
        <p className="catalog-heading__summary">
          Showing {Math.min(visibleCount, filteredProducts.length)} of {filteredProducts.length} item
          {filteredProducts.length === 1 ? "" : "s"}
        </p>
      </div>

      <main className="product-grid" id="ae-catalog-grid">
        {filteredProducts.slice(0, visibleCount).map((product) => (
          <article key={product.id} className="product-card">
            <button
              type="button"
              className="product-card__image-button"
              onClick={() => navigate(`/product/${product.id}`)}
              aria-label={`View details for ${product.name}`}
            >
              {isVideoUrl(product.imageUrl) ? (
                <video src={`${SHOP_IMAGE_BASE}${product.imageUrl}`} muted playsInline preload="metadata" />
              ) : (
                <img src={`${SHOP_IMAGE_BASE}${product.imageUrl}`} alt={product.name} />
              )}
            </button>
            <button
              type="button"
              className="product-card__title-button"
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <h3>{product.name}</h3>
            </button>
            {product.displayBrand && <p className="product-card__brand">{product.displayBrand}</p>}
            <p className="product-card__meta">
              <span>{product.karatLabel}</span>
              <span>{product.displayCategory}</span>
            </p>
            <p>PHP {formatPrice(product.pricePHP)}</p>
            <p className="description">{product.description}</p>
          </article>
        ))}
      </main>

      {!filteredProducts.length ? (
        <div className="catalog-empty-state">
          No available items matched your search for this account executive.
        </div>
      ) : null}

      {visibleCount < filteredProducts.length ? (
        <div style={{ textAlign: "center", margin: "0 0 2rem" }}>
          <button
            type="button"
            className="add-to-cart-btn"
            onClick={() => setVisibleCount((value) => value + 20)}
          >
            Show More
          </button>
        </div>
      ) : null}
    </div>
  );
}
